import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';

export const LANGUAGE_STORAGE_KEY = 'tgbi.language';

export const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ro', label: 'Română', flag: '🇷🇴' },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]['code'];

/**
 * English is bundled; every other language is fetched when it is first needed.
 *
 * The fallback language has to be in the first chunk — a round trip on first
 * paint to find out what the buttons say is a bad trade, and the offline shell
 * (spec §8.10) would otherwise be untranslated exactly when it matters. The
 * others are a lazy import: a reader who never switches never downloads them,
 * and the switch itself is a deliberate act that can afford a moment.
 */
export const resources = { en: { translation: en } } as const;

const LAZY_LOADERS: Record<string, () => Promise<{ default: unknown }>> = {
  ro: () => import('./locales/ro.json'),
};

/** Fetches a locale the bundle does not already carry, once. */
async function ensureLoaded(language: string): Promise<void> {
  const base = language.split('-')[0] ?? language;
  const load = LAZY_LOADERS[base];
  if (!load || i18next.hasResourceBundle(base, 'translation')) return;

  const module = await load();
  i18next.addResourceBundle(base, 'translation', module.default, true, true);
}

void i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: LANGUAGES.map((language) => language.code),
    // "ro-RO" from the browser should resolve to "ro", not fall through to English.
    nonExplicitSupportedLngs: true,
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      // React escapes for us; escaping again turns an apostrophe into &#39;.
      escapeValue: false,
    },
  });

/** Keeps `<html lang>` in step, which is what a screen reader reads it from. */
i18next.on('languageChanged', (language) => {
  document.documentElement.setAttribute('lang', language);
  void ensureLoaded(language);
});

/**
 * Loads whatever the detector picked, before anything renders.
 *
 * Without this a Romanian reader would see one frame of English on a hard
 * reload — the detector resolves synchronously, the bundle does not.
 */
export const languageReady = ensureLoaded(i18next.language);

export default i18next;
